import { createApi } from '@reduxjs/toolkit/query/react';

import {
  DELETE,
  GET,
  GET_GLOBAL_FILTER_FUNNELS,
  LIST_FOR_FILTERS,
  POST,
  PUT,
  SALES_FUNNELS_CHUNK,
} from '@app/constants';
import type { SalesFunnelDto } from '@app/pages/SalesFunnels/types';
import type { IBaseQueryParams } from '@app/services/rtkQuery/baseQuery';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type {
  GetSalesFunnelLeadsRequest,
  ModelSalesFunnelLeadsSearchResult,
} from '@app/services/rtkQuery/SalesFunnels/types';
import type { NAuth, NFunnels, NLogs } from '@app/types';

import type { AxiosHeaders } from 'axios';

const API_URL_PART = SALES_FUNNELS_CHUNK;

export const salesFunnelsApi = createApi({
  reducerPath: 'salesFunnelsApi',
  baseQuery: (props) => customBaseQuery(props),
  tagTypes: ['Funnels', 'ActionHistory'],
  endpoints: ({ query, mutation }) => ({
    [GET_GLOBAL_FILTER_FUNNELS]: query<
      NFunnels.SalesFunnelFilterDto[],
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        headers?: NLogs.Result | unknown;
      }
    >({
      query: ({ ActiveBranchId, headers = {} }) => ({
        url: `${API_URL_PART}/${LIST_FOR_FILTERS}`,
        method: GET,
        params: { ActiveBranchId },
        headers: headers as AxiosHeaders,
      }),
    }),
    List: query<
      NFunnels.SalesFunnelListDto[],
      {
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
        logs?: IBaseQueryParams['logs'];
      }
    >({
      query: ({ ActiveBranchId, logs }) => ({
        url: `${API_URL_PART}/List`,
        method: 'GET',
        params: { ActiveBranchId },
        logs,
      }),
      providesTags: [{ type: 'Funnels', id: 'LIST' }],
    }),
    GetSalesFunnel: query<
      SalesFunnelDto,
      {
        id: string | null;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${API_URL_PART}/Get/${id}`,
        method: GET,
        params,
      }),
      providesTags: (response, _error, { id }) =>
        response ? [{ type: 'Funnels', id: id ?? 'LIST' }] : ['Funnels'],
    }),
    DeleteSalesFunnel: mutation<
      void,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${API_URL_PART}/Delete/${id}`,
        method: DELETE,
        params,
      }),
    }),
    UpdateSalesFunnel: mutation<
      NFunnels.SalesFunnelDto,
      {
        id: number;
        data: NFunnels.WriteSalesFunnelDto;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, data, ActiveBranchId }) => ({
        url: `${API_URL_PART}/Update/${id}`,
        method: PUT,
        data,
        params: { ActiveBranchId },
      }),
      invalidatesTags: (_response, _error, { id }) => [
        { type: 'Funnels', id },
        { type: 'Funnels', id: 'LIST' },
      ],
    }),
    CreateSalesFunnel: mutation<
      NFunnels.SalesFunnelDto,
      { activeBranchId: number; data: NFunnels.WriteSalesFunnelDto }
    >({
      query: ({ activeBranchId, data }) => ({
        url: `${API_URL_PART}/Create`,
        method: POST,
        params: { activeBranchId },
        data,
      }),
    }),
    CheckForLeads: query<
      boolean,
      {
        id: number;
        ActiveBranchId: NAuth.UserPrint['activeBranchId'];
      }
    >({
      query: ({ id, ...params }) => ({
        url: `${API_URL_PART}/CheckForLeads/${id}`,
        method: GET,
        params,
      }),
      providesTags: (response, _error, { id }) =>
        response ? [{ type: 'Funnels', id: id ?? 'LIST' }] : ['Funnels'],
    }),
    UpdateStageForLead: mutation<void, NFunnels.UpdateStageForLeadRequest>({
      query: ({ leadId, ...params }) => ({
        url: `${API_URL_PART}/Stages/Leads/Exchange/${leadId}`,
        method: PUT,
        params,
      }),
    }),
    DeleteStageFromLead: mutation<void, NFunnels.DeleteStageForLeadRequest>({
      query: ({ leadId, ...params }) => ({
        url: `${API_URL_PART}/Stages/Leads/Delete/${leadId}`,
        method: DELETE,
        params,
      }),
    }),
    AddStageForLead: mutation<
      NFunnels.SalesFunnelStageFilterDto,
      NFunnels.AddStageForLeadRequest
    >({
      query: ({ ...params }) => ({
        url: `${API_URL_PART}/Stages/Leads/Add`,
        method: POST,
        params,
      }),
    }),
    GetSalesFunnelLeads: mutation<
      ModelSalesFunnelLeadsSearchResult,
      GetSalesFunnelLeadsRequest
    >({
      query: ({ data, ...params }) => ({
        url: `${API_URL_PART}/GetSalesFunnelLeads`,
        method: POST,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useGetGlobalFilterFunnelsQuery,
  useListQuery,
  useLazyListQuery,
  useGetSalesFunnelQuery,
  useDeleteSalesFunnelMutation,
  useUpdateSalesFunnelMutation,
  useCreateSalesFunnelMutation,
  useLazyCheckForLeadsQuery,
  useUpdateStageForLeadMutation,
  useDeleteStageFromLeadMutation,
  useAddStageForLeadMutation,
  useGetSalesFunnelLeadsMutation,
} = salesFunnelsApi;
